import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { searchFashion } from '../api/rakuten_service';
import './Analyzing.css';

/**
 * Analyzing Page - Clean, Professional Design
 * Premium aesthetic with aurora background and robot animation
 */

const ANALYSIS_STEPS = [
  { id: 'analyzing', title: 'Analyzing Image' },
  { id: 'building', title: 'Building Style Profile' },
  { id: 'searching', title: 'Finding Matches' }
];

const Analyzing = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const hasSearched = useRef(false);

  const image = location.state?.image;

  // Progress through steps - timings match longer API timeout
  useEffect(() => {
    if (isComplete || error) return;
    const stepTimings = [4000, 6000, 5000]; // Increased from [2000, 3000, 2000]
    let timeout;
    if (currentStep < ANALYSIS_STEPS.length - 1) {
      timeout = setTimeout(() => setCurrentStep(prev => prev + 1), stepTimings[currentStep]);
    }
    return () => clearTimeout(timeout);
  }, [currentStep, isComplete, error]);

  // Main search - fires exactly once using ref guard
  useEffect(() => {
    if (!image) {
      navigate('/', { replace: true });
      return;
    }

    // Guard: prevent double-firing (React StrictMode, re-renders, transition re-mounts)
    if (hasSearched.current) return;
    hasSearched.current = true;

    let cancelled = false;

    const performSearch = async () => {
      try {
        const results = await searchFashion(image);
        if (cancelled) return;
        setIsComplete(true);
        setTimeout(() => {
          navigate('/results', { state: { image, results }, replace: true });
        }, 600);
      } catch (err) {
        if (cancelled) return;
        console.error('Search failed:', err);
        setError(err.message || 'Something went wrong');
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    performSearch();

    return () => { cancelled = true; };
  }, [image]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="analyzing-page">
        <AuroraBackground />
        <motion.div
          className="analyzing-error"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="error-icon">✕</div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <span className="redirect-text">Redirecting...</span>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="analyzing-page">
      <AuroraBackground />
      <FloatingHexagons />

      <div className="analyzing-content">
        {/* Premium Central Loader - Concentric Rings + Logo */}
        <motion.div
          className="central-loader"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
            <div className="rings-container">
                <div className="ring ring-outer" />
                <div className="ring ring-inner" />
            </div>

            <div className="logo-center">
                 <svg className="logo-svg" viewBox="0 0 100 100">
                    <polygon
                      points="50,5 93,27.5 93,72.5 50,95 7,72.5 7,27.5"
                      className="logo-hex-outer"
                    />
                    <polygon
                      points="50,15 83,32.5 83,67.5 50,85 17,67.5 17,32.5"
                      className="logo-hex-inner"
                    />
                    <line x1="50" y1="15" x2="50" y2="50" className="logo-spoke" />
                    <line x1="83" y1="32.5" x2="50" y2="50" className="logo-spoke" />
                    <line x1="83" y1="67.5" x2="50" y2="50" className="logo-spoke" />
                    <line x1="50" y1="85" x2="50" y2="50" className="logo-spoke" />
                    <line x1="17" y1="67.5" x2="50" y2="50" className="logo-spoke" />
                    <line x1="17" y1="32.5" x2="50" y2="50" className="logo-spoke" />
                 </svg>
                 <div className="logo-glow" />
            </div>
        </motion.div>

        {/* Title */}
        <div className="text-container">
            <motion.h1
              className="analyzing-title"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {isComplete ? 'Perfect Matches Found' : 'Analyzing Your Style'}
            </motion.h1>
            <motion.p
                className="analyzing-subtitle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                {ANALYSIS_STEPS[currentStep]?.title || 'Finalizing...'}
            </motion.p>
        </div>

        {/* Robot Animation */}
        <RobotWithClothes />
      </div>
    </div>
  );
};

/**
 * Aurora Background - Premium, Calm, Abstract
 */
const AuroraBackground = () => {
  return (
    <div className="aurora-bg">
        <div className="aurora-blob blob-purple" />
        <div className="aurora-blob blob-light" />
        <div className="aurora-grain" />
    </div>
  );
};

/**
 * Floating Hexagons - Semi-transparent floating hexagons for visual interest
 */
const FloatingHexagons = () => {
  const hexagons = [
    // Large decorative hexagons - MORE VISIBLE
    { id: 1, size: 180, x: '5%', y: '10%', delay: 0, duration: 35, opacity: 0.12 },
    { id: 2, size: 220, x: '70%', y: '5%', delay: 5, duration: 40, opacity: 0.10 },
    { id: 3, size: 160, x: '80%', y: '60%', delay: 8, duration: 32, opacity: 0.14 },
    { id: 4, size: 200, x: '15%', y: '70%', delay: 3, duration: 38, opacity: 0.11 },
    // Medium hexagons
    { id: 5, size: 100, x: '50%', y: '25%', delay: 2, duration: 28, opacity: 0.15 },
    { id: 6, size: 90, x: '90%', y: '35%', delay: 6, duration: 30, opacity: 0.13 },
    { id: 7, size: 80, x: '25%', y: '45%', delay: 4, duration: 26, opacity: 0.14 },
    { id: 8, size: 110, x: '60%', y: '80%', delay: 7, duration: 33, opacity: 0.12 },
    // Small accent hexagons
    { id: 9, size: 50, x: '40%', y: '15%', delay: 1, duration: 22, opacity: 0.18 },
    { id: 10, size: 45, x: '85%', y: '85%', delay: 9, duration: 25, opacity: 0.16 },
    { id: 11, size: 55, x: '10%', y: '55%', delay: 10, duration: 24, opacity: 0.17 },
    { id: 12, size: 40, x: '95%', y: '15%', delay: 11, duration: 20, opacity: 0.19 },
  ];

  return (
    <div className="floating-hexagons">
      {hexagons.map((hex) => (
        <motion.div
          key={hex.id}
          className="floating-hex"
          style={{
            width: hex.size,
            height: hex.size,
            left: hex.x,
            top: hex.y,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: [hex.opacity * 0.6, hex.opacity, hex.opacity * 0.6],
            scale: [0.95, 1.05, 0.95],
            rotate: [0, 60, 120],
            y: [0, -10, 0],
          }}
          transition={{
            duration: hex.duration,
            delay: hex.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <svg viewBox="0 0 100 100" className="hex-svg">
            <polygon
              points="50,5 93,27.5 93,72.5 50,95 7,72.5 7,27.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <polygon
              points="50,20 78,35 78,65 50,80 22,65 22,35"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.8"
            />
            {/* Inner detail lines - logo style */}
            <line x1="50" y1="20" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
            <line x1="78" y1="35" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
            <line x1="78" y1="65" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
            <line x1="50" y1="80" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
            <line x1="22" y1="65" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
            <line x1="22" y1="35" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
};

/**
 * Robot With Clothes Animation
 * Clean, minimal robot running back and forth carrying clothes
 * MUCH SLOWER animation for relaxed, professional feel
 */
const RobotWithClothes = () => {
  const [clothes, setClothes] = useState([]);

  useEffect(() => {
    // Spawn a new cloth item every 4 seconds (slower, more relaxed)
    const interval = setInterval(() => {
      const id = Date.now();
      const type = ['shirt', 'dress', 'hanger'][Math.floor(Math.random() * 3)];
      setClothes(prev => [...prev, { id, type }]);

      // Remove after animation completes (longer duration)
      setTimeout(() => {
        setClothes(prev => prev.filter(c => c.id !== id));
      }, 14000);
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      className="robot-scene"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
    >
      {/* Ground line */}
      <div className="robot-ground" />

      {/* Animated robots with clothes - MUCH SLOWER animation */}
      <AnimatePresence>
        {clothes.map((item, index) => (
          <motion.div
            key={item.id}
            className="robot-runner"
            initial={{ x: index % 2 === 0 ? '-100px' : 'calc(100vw + 50px)' }}
            animate={{ x: index % 2 === 0 ? 'calc(100vw + 50px)' : '-100px' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 12, ease: "linear" }}
          >
            {/* Robot body */}
            <div className="robot-body">
              <div className="robot-head">
                <div className="robot-eye left" />
                <div className="robot-eye right" />
                <div className="robot-antenna" />
              </div>
              <div className="robot-torso" />
              <div className="robot-legs">
                <div className="robot-leg left" />
                <div className="robot-leg right" />
              </div>
            </div>

            {/* Cloth being carried */}
            <div className={`robot-cloth cloth-${item.type}`}>
              <ClothIcon type={item.type} />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
};

const ClothIcon = ({ type }) => {
  const icons = {
    shirt: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C10.3431 2 9 3.34315 9 5H4L2 9L4 10V21H20V10L22 9L20 5H15C15 3.34315 13.6569 2 12 2ZM12 4C12.5523 4 13 4.44772 13 5C13 5.55228 12.5523 6 12 6C11.4477 6 11 5.55228 11 5C11 4.44772 11.4477 4 12 4Z" />
      </svg>
    ),
    dress: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L9 4V6H8L6 10L8 11V20H16V11L18 10L16 6H15V4L12 2ZM12 4.5L13 5.5V6H11V5.5L12 4.5Z" />
      </svg>
    ),
    hanger: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4C10.9 4 10 4.9 10 6C10 6.74 10.4 7.39 11 7.73V8L3 14V16H21V14L13 8V7.73C13.6 7.39 14 6.74 14 6C14 4.9 13.1 4 12 4ZM12 6C12 6 12 6 12 6C12 6 12 6 12 6Z" />
      </svg>
    )
  };
  return icons[type] || icons.shirt;
};

export default Analyzing;

